import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Trees,
  Cpu,
  Radio,
  Droplets,
  Wifi,
  WifiOff,
  Gauge,
  TrendingDown,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { StatCard } from '@/components/dashboard/StatCard';
import { WeatherWidget } from '@/components/dashboard/WeatherWidget';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { UpcomingIrrigations } from '@/components/dashboard/UpcomingIrrigations';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardService, gardensService, controllersService, sensorsService } from '@/services';
import { useAuth } from '@/contexts/AuthContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Skeleton component for stats
function StatCardSkeleton() {
  return (
    <div className="p-6 rounded-xl border bg-card">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-12 w-12 rounded-xl" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentUser, isSuperAdmin, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Si aún se está cargando la autenticación, mostrar loading
  if (authLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Cargando...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }
  
  // Si no está autenticado, no renderizar (ProtectedRoute debería redirigir)
  if (!isAuthenticated || !currentUser) {
    return null;
  }
  
  // Obtener companyId si el usuario es company_admin
  const companyId = currentUser.role === 'company_admin' ? currentUser.companyId : undefined;
  
  // No ejecutar queries si el usuario no está autenticado o aún se está cargando
  // Asegurar que siempre sea un booleano explícito (ahora siempre será true porque ya verificamos arriba)
  const shouldFetch = true;

  // Query para obtener estadísticas del dashboard
  // El backend debería filtrar automáticamente según el usuario autenticado
  const { 
    data: statsData, 
    isLoading: statsLoading, 
    error: statsError,
    refetch: refetchStats 
  } = useQuery({
    queryKey: ['dashboard-stats', companyId],
    queryFn: async () => {
      const response = await dashboardService.getStats();
      if (!response.success) {
        // Si es error de autenticación, lanzar error específico
        if (response.error?.code === 'UNAUTHORIZED' || response.error?.message?.includes('401')) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(response.error?.message || 'Error al cargar estadísticas');
      }
      return response.data;
    },
    enabled: shouldFetch, // Solo ejecutar si el usuario está autenticado
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Query para obtener jardines (para conteo) - filtrado por empresa si es company_admin
  const { data: gardensData } = useQuery({
    queryKey: ['gardens-count', companyId],
    queryFn: async () => {
      const response = await gardensService.getAll({ 
        pageSize: 1,
        ...(companyId && { companyId }),
      });
      if (!response.success) {
        if (response.error?.code === 'UNAUTHORIZED' || response.error?.message?.includes('401')) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(response.error?.message || 'Error al cargar jardines');
      }
      return response.data;
    },
    enabled: shouldFetch,
  });

  // Query para obtener controladoras (para conteo y estado) - filtrado por empresa si es company_admin
  const { data: controllersData } = useQuery({
    queryKey: ['controllers-count', companyId],
    queryFn: async () => {
      const response = await controllersService.getAll({ 
        pageSize: 100,
        ...(companyId && { companyId }),
      });
      if (!response.success) {
        if (response.error?.code === 'UNAUTHORIZED' || response.error?.message?.includes('401')) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(response.error?.message || 'Error al cargar controladoras');
      }
      return response.data;
    },
    enabled: shouldFetch,
  });

  // Query para obtener sensores (para conteo) - filtrado por empresa si es company_admin
  const { data: sensorsData } = useQuery({
    queryKey: ['sensors-count', companyId],
    queryFn: async () => {
      const response = await sensorsService.getAll({ 
        pageSize: 100,
        ...(companyId && { companyId }),
      });
      if (!response.success) {
        if (response.error?.code === 'UNAUTHORIZED' || response.error?.message?.includes('401')) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(response.error?.message || 'Error al cargar sensores');
      }
      return response.data;
    },
    enabled: shouldFetch,
  });

  // Query para obtener jardines (para meteorología) - filtrado por empresa si es company_admin
  const { data: gardensForWeather } = useQuery({
    queryKey: ['gardens-weather', companyId],
    queryFn: async () => {
      const response = await gardensService.getAll({ 
        pageSize: 100,
        ...(companyId && { companyId }),
      });
      if (!response.success) {
        if (response.error?.code === 'UNAUTHORIZED' || response.error?.message?.includes('401')) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(response.error?.message || 'Error al cargar jardines');
      }
      return response.data;
    },
    enabled: shouldFetch,
  });

  // Obtener el primer jardín para mostrar su meteorología
  const firstGarden = gardensForWeather?.items?.[0];
  
  // Query para obtener datos meteorológicos del primer jardín
  const { data: weatherData } = useQuery({
    queryKey: ['weather-dashboard', firstGarden?.id],
    queryFn: async () => {
      if (!firstGarden?.id) return null;
      try {
        const response = await gardensService.getWeather(firstGarden.id, 4);
        if (!response.success) {
          return null;
        }
        return response.data;
      } catch (e) {
        console.warn('Weather endpoint error:', e);
        return null;
      }
    },
    enabled: !!firstGarden?.id,
    retry: false,
  });

  // Calculate stats from API data
  const controllers = controllersData?.items || [];
  const sensors = sensorsData?.items || [];
  const totalGardens = gardensData?.total || 0;
  const totalControllers = controllersData?.total || 0;
  const totalSensors = sensorsData?.total || 0;
  
  const onlineControllers = controllers.filter(c => c.status === 'online').length;
  const onlineSensors = sensors.filter(s => s.status === 'online').length;
  const onlineDevices = onlineControllers + onlineSensors;
  
  // Use the actual count of items returned, not the total, to avoid negative values
  // This ensures we calculate offline devices based on the devices we actually have data for
  const totalDevicesInView = controllers.length + sensors.length;
  const offlineDevices = Math.max(0, totalDevicesInView - onlineDevices);

  // Use API stats if available, otherwise calculate from data
  const stats = statsData || {
    totalGardens,
    totalControllers,
    totalSensors,
    onlineDevices,
    offlineDevices,
    activeIrrigations: 0,
    averageHumidity: 0,
    waterSavedToday: 0,
  };

  const isLoading = statsLoading;
  const error = statsError;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Vista general del sistema de riego</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <div className="lg:col-span-2">
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Vista general del sistema de riego</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar estadísticas'}</span>
            <Button variant="outline" size="sm" onClick={() => refetchStats()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Vista general del sistema de riego
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          Sistema operativo
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Jardines"
          value={stats.totalGardens || totalGardens}
          subtitle="Gestionados activamente"
          icon={Trees}
          variant="primary"
        />
        <StatCard
          title="Controladoras"
          value={stats.totalControllers || totalControllers}
          subtitle={`${onlineControllers} online`}
          icon={Cpu}
          variant="success"
        />
        <StatCard
          title="Sensores"
          value={stats.totalSensors || totalSensors}
          subtitle="EM500-SMTC activos"
          icon={Radio}
          variant="accent"
        />
        <StatCard
          title="Riegos Activos"
          value={stats.activeIrrigations || 0}
          subtitle="En ejecución ahora"
          icon={Droplets}
          variant="default"
          trend={{ value: 12, isPositive: false }}
        />
      </div>

      {/* Second row - more stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Dispositivos Online"
          value={stats.onlineDevices || onlineDevices}
          icon={Wifi}
          variant="success"
        />
        <StatCard
          title="Dispositivos Offline"
          value={stats.offlineDevices || offlineDevices}
          icon={WifiOff}
          variant={(stats.offlineDevices || offlineDevices) > 0 ? 'danger' : 'default'}
        />
        <StatCard
          title="Humedad Media"
          value={`${stats.averageHumidity || 0}%`}
          subtitle="Últimas 24 horas"
          icon={Gauge}
          variant="accent"
        />
        <StatCard
          title="Agua Ahorrada"
          value={`${((stats.waterSavedToday || 0) / 1000).toFixed(1)}m³`}
          subtitle="Hoy vs riego tradicional"
          icon={TrendingDown}
          variant="primary"
          trend={{ value: 8, isPositive: true }}
        />
      </div>

      {/* Upcoming Irrigations and Weather */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UpcomingIrrigations />
        {weatherData && firstGarden ? (
          <WeatherWidget 
            weather={[
              // Incluir current como hoy
              ...(weatherData.current ? [{
                municipality: weatherData.municipality || firstGarden.municipality || 'N/A',
                date: new Date(),
                temperature: weatherData.current.temperature,
                humidity: weatherData.current.humidity,
                precipitation: weatherData.current.precipitation || 0,
                precipitationProbability: weatherData.current.precipitationProbability || 0,
                windSpeed: weatherData.current.windSpeed || 0,
                description: weatherData.current.description,
                icon: weatherData.current.icon,
              }] : []),
              // Incluir forecast para los próximos días
              ...weatherData.forecast.map((day) => ({
                municipality: weatherData.municipality || firstGarden.municipality || 'N/A',
                date: new Date(day.date),
                temperature: day.temperature,
                humidity: day.humidity,
                precipitation: day.precipitation || 0,
                precipitationProbability: day.precipitationProbability || 0,
                windSpeed: day.windSpeed || 0,
                description: day.description,
                icon: day.icon,
              })),
            ]}
          />
        ) : (
          <WeatherWidget 
            weather={[{
              municipality: firstGarden?.municipality || 'Sin datos',
              date: new Date(),
              temperature: { min: 0, max: 0 },
              humidity: 0,
              precipitation: 0,
              precipitationProbability: 0,
              windSpeed: 0,
              description: 'Cargando datos meteorológicos...',
              icon: 'cloud',
            }]}
          />
        )}
      </div>

      {/* Recent Activity */}
      <RecentActivity />
    </motion.div>
  );
}
